import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { featuredVenues } from "../../../mocks/venues";

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <div key={star} className="w-3 h-3 flex items-center justify-center">
          <i
            className={`ri-star-fill text-xs ${
              star <= Math.floor(rating) ? "text-amber-400" : "text-stone-200"
            }`}
          />
        </div>
      ))}
    </div>
  );
}

export default function FeaturedSection() {
  const { t } = useTranslation();

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-stone-100 text-stone-600 text-xs font-semibold font-body uppercase tracking-wider mb-4">
              {t("featured_badge")}
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-anthracite-900">
              {t("featured_title")}
            </h2>
            <p className="text-anthracite-400 text-base mt-2 font-body max-w-xl">
              {t("featured_subtitle")}
            </p>
          </div>
          <Link
            to="/category/featured"
            className="inline-flex items-center gap-2 text-sm font-semibold text-forest-700 hover:text-forest-600 font-body whitespace-nowrap cursor-pointer group"
          >
            {t("featured_view_all")}
            <div className="w-5 h-5 flex items-center justify-center rounded-full border border-forest-700 group-hover:bg-forest-700 group-hover:text-white transition-all">
              <i className="ri-arrow-right-line text-xs" />
            </div>
          </Link>
        </div>

        {/* Venue Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {featuredVenues.map((venue, index) => (
            <Link
              key={venue.id}
              to={`/venue/${venue.id}`}
              className={`group bg-white border border-stone-100 rounded-2xl overflow-hidden cursor-pointer block transition-all hover:-translate-y-1 ${
                index === 0 ? "sm:col-span-2 lg:col-span-1" : ""
              }`}
            >
              {/* Image */}
              <div className="relative overflow-hidden h-48">
                <img
                  src={venue.image}
                  alt={venue.name}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                {/* Badges */}
                <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
                  {venue.isSponsored && (
                    <span className="px-2.5 py-1 rounded-full bg-amber-500 text-white text-xs font-semibold font-body">
                      {t("featured_sponsored")}
                    </span>
                  )}
                  {venue.isEditorPick && !venue.isSponsored && (
                    <span className="px-2.5 py-1 rounded-full bg-forest-600 text-white text-xs font-semibold font-body">
                      {t("featured_editor")}
                    </span>
                  )}
                </div>
                {/* Open badge */}
                <div className="absolute top-3 right-3">
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-medium font-body ${
                      venue.isOpen
                        ? "bg-forest-50 text-forest-700"
                        : "bg-stone-100 text-stone-500"
                    }`}
                  >
                    {venue.isOpen ? t("featured_open") : t("featured_closed")}
                  </span>
                </div>
              </div>

              {/* Info */}
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-body text-forest-600 font-semibold bg-forest-50 px-2 py-0.5 rounded-full">
                    {venue.category}
                  </span>
                  <span className="text-xs text-stone-400 font-body">
                    {venue.distanceKm} {t("featured_km_away")}
                  </span>
                </div>

                <h3 className="font-display font-bold text-anthracite-900 text-base mb-1.5 group-hover:text-forest-700 transition-colors">
                  {venue.name}
                </h3>

                <p className="text-anthracite-400 text-xs font-body leading-relaxed mb-3 line-clamp-2">
                  {venue.description}
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <StarRating rating={venue.rating} />
                    <span className="text-xs font-semibold text-anthracite-700">{venue.rating}</span>
                    <span className="text-xs text-stone-400 font-body">({venue.reviews.toLocaleString()})</span>
                  </div>
                </div>

                <div className="flex gap-2 mt-3 pt-3 border-t border-stone-100">
                  <button className="flex-1 py-1.5 text-xs font-semibold text-forest-700 border border-forest-200 rounded-lg hover:bg-forest-50 transition-colors cursor-pointer whitespace-nowrap">
                    {t("featured_details")}
                  </button>
                  <button className="flex-1 py-1.5 text-xs font-semibold text-white bg-forest-600 rounded-lg hover:bg-forest-500 transition-colors cursor-pointer whitespace-nowrap">
                    {t("featured_directions")}
                  </button>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
